/*!
* @byted/date-fmt v2.1.1
* (c) 2021 thomas.cheng
*/
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

var classCallCheck = _classCallCheck;

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

var createClass = _createClass;

var baseLocaleConfig = {
  name: 'en',
  months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  weekdaysMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  weekdaysShort: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  formats: {
    lt: 'h:mm A',
    lts: 'h:mm:ss A',
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
    L: 'MM/DD/YYYY',
    LL: 'MMMM D, YYYY',
    'LL-D': 'MMMM YYYY',
    'LL-Y': 'MMMM D',
    LLL: 'MMMM D, YYYY HH:mm',
    'LLL-Y': 'MMMM D HH:mm',
    LLLL: 'dddd MMMM D, YYYY HH:mm',
    'LLLL-Y': 'dddd MMMM D HH:mm',
    l: 'M/D/YYYY',
    ll: 'MMM D, YYYY',
    'll-Y': 'MMM D',
    lll: 'MMM D, YYYY h:mm A',
    'lll-Y': 'MMM D h:mm A',
    llll: 'ddd MMM D, YYYY h:mm A',
    'llll-Y': 'ddd MMM D h:mm A'
  },
  meridiem: {
    am: 'am',
    pm: 'pm',
    AM: 'AM',
    PM: 'PM'
  },
  past: {
    y: {
      one: '%s year ago',
      other: '%s years ago'
    },
    M: {
      one: '%s month ago',
      other: '%s months ago'
    },
    w: {
      one: '%s week ago',
      other: '%s weeks ago'
    },
    d: {
      one: '%s day ago',
      other: '%s days ago'
    },
    h: {
      one: '%s hour ago',
      other: '%s hours ago'
    },
    m: {
      one: '%s minute ago',
      other: '%s minutes ago'
    },
    s: {
      one: '%s second ago',
      other: '%s seconds ago'
    }
  },
  future: {
    y: {
      one: 'in %s year',
      other: 'in %s years'
    },
    M: {
      one: 'in %s month',
      other: 'in %s months'
    },
    w: {
      one: 'in %s week',
      other: 'in %s weeks'
    },
    d: {
      one: 'in %s day',
      other: 'in %s days'
    },
    h: {
      one: 'in %s hour',
      other: 'in %s hours'
    },
    m: {
      one: 'in %s minute',
      other: 'in %s minutes'
    },
    s: {
      one: 'in %s second',
      other: 'in %s seconds'
    }
  },
  abbr: {
    y: {
      one: '%syr.',
      other: '%syr.'
    },
    M: {
      one: '%sm',
      other: '%sm'
    },
    w: {
      one: '%sw',
      other: '%sw'
    },
    d: {
      one: '%sd',
      other: '%sd'
    },
    h: {
      one: '%sh',
      other: '%sh'
    },
    m: {
      one: '%smin',
      other: '%smin'
    },
    s: {
      one: '%ss',
      other: '%ss'
    }
  },
  justNow: 'just now',
  yesterday: 'Yesterday',
  today: 'Today',
  tomorrow: 'Tomorrow'
};
var localeName = 'en';
var localeConfig = Object.assign(Object.assign({}, baseLocaleConfig), {
  name: localeName,
  meridiem: function meridiem(hour, _) {
    var isLowerCase = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

    if (hour < 12) {
      return isLowerCase ? 'am' : 'AM';
    } else {
      return isLowerCase ? 'pm' : 'PM';
    }
  }
});

var S = 'second';
var MIN = 'minute';
var H = 'hour';
var D = 'day';
var W = 'week';
var M = 'month';

var ZERO = 'zero';
var ONE = 'one';
var TWO = 'two';
var FEW = 'few';
var MANY = 'many';
var OTHER = 'other'; // threshold configs

var DEFAULT_THRESHOLDS = [{
  relativeTimeKey: 'y',
  num: 11,
  unit: M
}, {
  relativeTimeKey: 'M',
  num: 3.5,
  unit: W
}, {
  relativeTimeKey: 'w',
  num: 6.5,
  unit: D
}, {
  relativeTimeKey: 'd',
  num: 22,
  unit: H
}, {
  relativeTimeKey: 'h',
  num: 45,
  unit: MIN
}, {
  relativeTimeKey: 'm',
  num: 45,
  unit: S
}, {
  relativeTimeKey: 's',
  num: 0,
  unit: S
}];
var LOCALE_NAMES_SET = new Set(['af', 'ar', 'bn', 'ceb', 'cs', 'de', 'el', 'en', 'es', 'fi', 'fil', 'fr', 'gu', 'he', 'hi', 'hu', 'id', 'it', 'ja', 'jv', 'km', 'kn', 'ko', 'ml', 'mr', 'ms', 'my', 'nl', 'or', 'pa', 'pl', 'pt', 'ro', 'ru', 'sv', 'sw', 'ta', 'te', 'th', 'tr', 'uk', 'vi', 'zh-Hans', 'zh-Hant']);

// refers to https://github.com/iamkun/dayjs/blob/dev/src/plugin/localizedFormat/index.js
var englishFormats = localeConfig.formats; // copy from the official plugin (https://github.com/iamkun/dayjs/blob/dev/src/plugin/localizedFormat/index.js)
// TikTok custom type 'LL-Y' is conficted with normal format which would be treated as LL(double L type) and Y(year)

var u = function u(formatStr, formats) {
  return formatStr.replace(/(\[[^\]]+])|(LTS?|lts?|L{2,4}\-Y|l{2,4}\-Y|LL\-D|l{1,4}|L{1,4})/g, function (_, a, b) {
    return a || formats[b] || englishFormats[b];
  });
};

var FORMAT_DEFAULT = 'YYYY-MM-DDTHH:mm:ssZ';

var plugin$2 = function plugin(_o, c, d) {
  var proto = c.prototype;
  var oldFormat = proto.format;
  d.Ls.en.formats = englishFormats;

  proto.format = function () {
    var formatStr = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : FORMAT_DEFAULT;

    var _this$$locale = this.$locale(),
        formats = _this$$locale.formats;

    var result = u(formatStr, formats);
    return oldFormat.call(this, result);
  };
};

var getPluralType = function getPluralType(num, locale) {
  switch (locale) {
    case 'ar':
      if (num === 0) {
        return ZERO;
      }

      if (num === 1) {
        return ONE;
      }

      if (num === 2) {
        return TWO;
      }

      if (num % 100 >= 3 && num % 100 <= 10) {
        return FEW;
      }

      if (num % 100 > 10) {
        return MANY;
      }

      return OTHER;

    case 'bn':
    case 'nl':
    case 'en':
    case 'fil':
    case 'fi':
    case 'de':
    case 'el':
    case 'hu':
    case 'it':
    case 'pt':
    case 'es':
    case 'sv':
    case 'ta':
    case 'tr':
      if (num === 1) {
        return ONE;
      }

      return OTHER;

    case 'ceb':
      if (num % 10 === 4 || num % 10 === 6 || num % 10 === 9) {
        return ONE;
      }

      return OTHER;

    case 'cs':
      if (num === 1) {
        return ONE;
      }

      if (num % 10 >= 2 && num % 10 <= 4) {
        return FEW;
      }

      return OTHER;

    case 'fr':
    case 'gu':
    case 'hi':
    case 'pa':
      if (num === 0 || num === 1) {
        return ONE;
      }

      return OTHER;

    case 'he':
      if (num === 1) {
        return ONE;
      }

      if (num === 2) {
        return TWO;
      }

      if (num !== 0 && num % 10 === 0) {
        return MANY;
      }

      return OTHER;

    case 'pl':
    case 'ru':
    case 'uk':
      if (num === 1) {
        return ONE;
      }

      if (num % 10 >= 2 && num % 10 <= 4) {
        return FEW;
      }

      return MANY;

    case 'ro':
      if (num === 1) {
        return ONE;
      }

      if (num === 0 || num % 100 >= 2 && num % 100 <= 19) {
        return FEW;
      }

      return OTHER;

    default:
      return OTHER;
  }
};

var plugin$1 = function plugin(options, dayjsClass, dayjs) {
  var proto = dayjsClass.prototype; // eslint-disable-next-line complexity

  var fromToBase = function fromToBase(input, instance, useAbbreviation) {
    var localeConfig = instance.$locale();
    var thresholds = options.thresholds,
        rounding = options.rounding;
    var durationStr = '';

    for (var i = 0; i < thresholds.length; i += 1) {
      var _thresholds$i = thresholds[i],
          relativeTimeKey = _thresholds$i.relativeTimeKey,
          num = _thresholds$i.num,
          unit = _thresholds$i.unit;
      var thresholdDiff = dayjs(input).diff(instance, unit, true);
      var absThresholdDiff = rounding(Math.abs(thresholdDiff));

      if (absThresholdDiff >= num) {
        var absDiff = rounding(Math.abs(dayjs(input).diff(instance, relativeTimeKey, true)));
        var pluralType = getPluralType(absDiff, instance.$locale().name);
        var tense = thresholdDiff > 0 ? 'future' : 'past';
        var templateMap = useAbbreviation ? localeConfig.abbr[relativeTimeKey] : localeConfig[tense][relativeTimeKey];
        var template = templateMap[pluralType] || '';
        durationStr = template.replace('%s', "".concat(absDiff));
        break;
      }
    }

    return durationStr;
  };

  proto.to = function (input) {
    var useAbbreviation = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    return fromToBase(input, this, useAbbreviation);
  };
};

// eslint-disable-next-line max-lines-per-function
var plugin = function plugin(options, dayjsClass, dayjs) {
  var proto = dayjsClass.prototype;
  var justNowThreshold = options.justNowThreshold; // eslint-disable-next-line complexity

  proto.getTimeExpression = function (start, end) {
    var startTime = dayjs(start);
    var endTime = dayjs(end);

    var _endTime$$locale = endTime.$locale(),
        justNow = _endTime$$locale.justNow,
        today = _endTime$$locale.today,
        tomorrow = _endTime$$locale.tomorrow,
        yesterday = _endTime$$locale.yesterday;

    var absDiff = Math.abs(startTime.diff(endTime, 'ms', true));

    if (absDiff <= justNowThreshold) {
      return justNow;
    }

    if (startTime.isSame(endTime, 'day')) {
      return today;
    }

    if (startTime.add(1, 'day').isSame(endTime, 'day')) {
      return tomorrow;
    }

    if (startTime.subtract(1, 'day').isSame(endTime, 'day')) {
      return yesterday;
    }

    return '';
  };
};
/**
 * used inside dayjs.extend to
 * register/unregister timeExpression plugin
 */


plugin.$i = false;

dayjs.extend(utc); // extend dayjs with advanced format

dayjs.extend(localizedFormat); // extend dayjs with custom format like 'LL-Y'

dayjs.extend(plugin$2);
dayjs.extend(plugin$1, {
  thresholds: DEFAULT_THRESHOLDS,
  rounding: Math.round
});
dayjs.extend(plugin, {
  justNowThreshold: 1000
});
dayjs.locale(localeConfig.name, localeConfig);
/** parse raw locale to supported locale */

function parseLocale(raw) {
  var lowerCased = raw.toLowerCase();

  if (lowerCased.startsWith('zh-hant')) {
    return 'zh-Hant';
  }

  if (lowerCased === 'zh-hans') {
    return 'zh-Hans';
  }

  var language = lowerCased.split('-')[0];

  if (LOCALE_NAMES_SET.has(language)) {
    return language;
  }

  return 'en';
}

var BDDateFmt = /*#__PURE__*/function () {
  function BDDateFmt() {
    classCallCheck(this, BDDateFmt);
  }

  createClass(BDDateFmt, [{
    key: "format",
    value:
    /** return formatted string */
    function format(dateOrTimestamp, type) {
      var date = this.getDate(dateOrTimestamp);
      return dayjs(date).format(type);
    }
    /** switch locale */

  }, {
    key: "setLocale",
    value: function setLocale(locale) {
      dayjs.locale(parseLocale(locale));
    }
    /** load locale config from CDN and set that locale */
    // loadLocale(locale: string): Promise<void> {}

    /* manually set and use LocaleConfig */

  }, {
    key: "setLocaleConfig",
    value: function setLocaleConfig(localeConfig) {
      dayjs.locale(localeConfig.name, localeConfig);
      return this;
    }
  }, {
    key: "utcFormat",
    value: function utcFormat(dateOrTimestamp, type) {
      var date = this.getDate(dateOrTimestamp);
      return dayjs(date).utc().format(type);
    }
    /**
     * returns future time when start < end
     * returns past time when start >= end
     */

  }, {
    key: "getRelativeTime",
    value: function getRelativeTime(start, end) {
      return dayjs(dayjs(start).utc()).to(dayjs(end).utc());
    }
  }, {
    key: "getRelativeTimeAbbr",
    value: function getRelativeTimeAbbr(start, end) {
      return dayjs(dayjs(start).utc()).to(dayjs(end).utc(), true);
    }
  }, {
    key: "configureTimeExpression",
    value: function configureTimeExpression(options) {
      plugin.$i = false;
      dayjs.extend(plugin, options);
    }
  }, {
    key: "getTimeExpression",
    value: function getTimeExpression(start, end) {
      return dayjs.prototype.getTimeExpression(start, end);
    }
  }, {
    key: "getDate",
    value: function getDate(dateOrTimestamp) {
      if (typeof dateOrTimestamp === 'number') {
        return new Date(dateOrTimestamp);
      }

      return dateOrTimestamp;
    }
  }]);

  return BDDateFmt;
}();

var index = new BDDateFmt();

export default index;
export { parseLocale };
//# sourceMappingURL=index.es.js.map
